import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAllPostsAdmin,
  fetchDrawers,
  setPostPublished,
  setPostSchedule,
  adminDeletePost,
  fetchFailedEncodes,
  type FailedEncode,
} from '../../lib/queries';
import type { Post, Drawer } from '../../lib/types';
import {
  fetchFeedCycle,
  saveFeedCycle,
  nextReset,
  resetDayInMonth,
  describeRule,
  type FeedCycleRule,
  type CadenceType,
} from '../../lib/feed-cycle';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../Skeleton';
import { EmptyState } from '../EmptyState';
import { useToast } from '../Toast';
import './ContentManager.css';

// ---------------------------------------------------------------------------
// Admin content manager. Replaces the flat post list with:
//  - status tabs (All / Draft / Scheduled / Published)
//  - a per-closet filter
//  - a List view (cards + inline schedule/publish/delete) and a Calendar view
//    (month grid plotting scheduled + published posts by go-live date).
//
// Status is derived from published_at - the same semantics the DB + RLS use:
//   null         → draft
//   future time  → scheduled (hidden until then; surfaces automatically)
//   past/now     → published (live)
// Scheduling is therefore just "set published_at to a future time".
// ---------------------------------------------------------------------------

type PostStatus = 'draft' | 'scheduled' | 'published';
type StatusTab = 'all' | PostStatus;
type ViewMode = 'list' | 'calendar';

function statusOf(post: Post, now: number): PostStatus {
  if (!post.published_at) return 'draft';
  return new Date(post.published_at).getTime() > now ? 'scheduled' : 'published';
}

const STATUS_LABEL: Record<PostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  published: 'Published',
};

/** Cover resolution mirrors coverUrl() in Closet/PostCard - an explicit
 *  per-post thumbnail wins, else the first media item. */
function coverOf(post: Post): string | null {
  if (post.thumbnail_url) return post.thumbnail_url;
  return post.media?.thumbnail_url ?? null;
}

// ---------- datetime-local helpers ----------------------------------------

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function defaultScheduleValue(): string {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  d.setDate(d.getDate() + 1);
  return toLocalInput(d);
}

// ---------- Calendar grid --------------------------------------------------

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_FMT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

interface MonthCell {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  posts: Post[];
}

function buildMonthCells(year: number, monthIndex: number, posts: Post[]): MonthCell[] {
  const today = new Date();
  const firstOfMonth = new Date(year, monthIndex, 1);
  const gridStart = new Date(year, monthIndex, 1 - firstOfMonth.getDay());

  const byDay = new Map<string, Post[]>();
  for (const p of posts) {
    if (!p.published_at) continue; // drafts have no day
    const d = new Date(p.published_at);
    const k = dayKey(d);
    const arr = byDay.get(k) ?? [];
    arr.push(p);
    byDay.set(k, arr);
  }

  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const dayPosts = (byDay.get(dayKey(d)) ?? [])
      .slice()
      .sort((a, b) => (a.published_at ?? '').localeCompare(b.published_at ?? ''));
    cells.push({
      date: d,
      inMonth: d.getMonth() === monthIndex,
      isToday: sameDay(d, today),
      posts: dayPosts,
    });
  }
  return cells;
}

// ---------- Component ------------------------------------------------------

export function ContentManager({ active }: { active: boolean }) {
  const toast = useToast();
  const [posts, setPosts] = useState<Post[]>([]);
  const [drawers, setDrawers] = useState<Drawer[]>([]);
  const [failed, setFailed] = useState<FailedEncode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [closetFilter, setClosetFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('list');
  const [busyId, setBusyId] = useState<string | null>(null);

  // Inline schedule editor (list + calendar detail share this).
  const [scheduleId, setScheduleId] = useState<string | null>(null);
  const [scheduleValue, setScheduleValue] = useState<string>(defaultScheduleValue());

  // Calendar position + selected post (detail popover).
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [selected, setSelected] = useState<Post | null>(null);

  // Drag-to-reschedule (scheduled posts only).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  // Feed reset cadence (global). 'off' = no monthly reset.
  const [cycleRule, setCycleRule] = useState<FeedCycleRule>(null);
  const [cycleType, setCycleType] = useState<'off' | CadenceType>('off');
  const [cycleDay, setCycleDay] = useState<number>(15);
  const [cycleSaving, setCycleSaving] = useState(false);

  // now() is captured per render-set so status is stable within a pass.
  const now = Date.now();

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [p, d, cyc] = await Promise.all([fetchAllPostsAdmin(), fetchDrawers(), fetchFeedCycle()]);
      setPosts(p);
      setDrawers(d);
      setCycleRule(cyc);
      setCycleType(cyc ? cyc.type : 'off');
      if (cyc?.type === 'day_of_month' && cyc.day) setCycleDay(cyc.day);
      // Failed video encodes - best-effort, fire-and-forget. Never let this
      // block or break the content load: the error_detail column / cf-stream
      // webhook may not be live in this environment yet.
      fetchFailedEncodes().then(setFailed).catch(() => setFailed([]));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load content.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  async function saveCadence() {
    const rule: FeedCycleRule =
      cycleType === 'off' ? null
        : cycleType === 'last_day' ? { type: 'last_day', day: null }
          : { type: 'day_of_month', day: cycleDay };
    setCycleSaving(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const stored = await saveFeedCycle(rule, token);
      setCycleRule(stored);
      setCycleType(stored ? stored.type : 'off');
      if (stored?.type === 'day_of_month' && stored.day) setCycleDay(stored.day);
      toast.success('Feed reset cadence updated. The wall picks it up on next load.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not save the cadence.');
    } finally {
      setCycleSaving(false);
    }
  }

  // Has the cadence control diverged from what's stored?
  const cadenceDirty =
    cycleType !== (cycleRule ? cycleRule.type : 'off') ||
    (cycleType === 'day_of_month' && cycleRule?.type === 'day_of_month' && cycleDay !== cycleRule.day);

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ---------- Filtering ----------
  const counts = useMemo(() => {
    const c = { all: posts.length, draft: 0, scheduled: 0, published: 0 };
    for (const p of posts) c[statusOf(p, now)]++;
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      if (statusTab !== 'all' && statusOf(p, now) !== statusTab) return false;
      if (closetFilter !== 'all' && !(p.drawers ?? []).some((d) => d.id === closetFilter)) return false;
      if (q) {
        const haystack = [p.title, p.dek, ...(p.drawers ?? []).map((d) => d.name)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, statusTab, closetFilter, search]);

  // Calendar uses the closet filter but ignores the status tab (it always shows
  // scheduled + published, which is what a publishing calendar is for).
  const calendarPosts = useMemo(() => {
    return posts.filter((p) => {
      if (!p.published_at) return false;
      if (closetFilter !== 'all' && !(p.drawers ?? []).some((d) => d.id === closetFilter)) return false;
      return true;
    });
  }, [posts, closetFilter]);

  const cells = useMemo(
    () => buildMonthCells(year, monthIndex, calendarPosts),
    [year, monthIndex, calendarPosts],
  );
  const resetDay = resetDayInMonth(cycleRule, year, monthIndex);

  // ---------- Mutations ----------
  function patchPost(id: string, published_at: string | null) {
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, published_at } : p)));
    setSelected((s) => (s && s.id === id ? { ...s, published_at } : s));
  }

  // live = true → published_at now (go live); false → null (back to draft).
  // Covers "Publish now", "Unpublish", and "Back to draft" (un-schedule).
  async function handleSetLive(post: Post, live: boolean) {
    setBusyId(post.id);
    try {
      const next = await setPostPublished(post.id, live);
      patchPost(post.id, next);
      toast.success(live ? 'Published, now live.' : 'Reverted to draft.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not update publish state.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleSchedule(post: Post) {
    if (!scheduleValue) return;
    const when = new Date(scheduleValue);
    if (Number.isNaN(when.getTime())) {
      toast.error('That date didn\'t parse. Pick a time again.');
      return;
    }
    const isPast = when.getTime() <= Date.now();
    // A past time means "publish immediately". Make that explicit (with the
    // resolved local time) instead of silently going live - the datetime-local
    // input is in the admin's local timezone.
    if (isPast && !window.confirm(
      `${when.toLocaleString()} is in the past, so this will publish immediately. Continue?`,
    )) {
      return;
    }
    const iso = when.toISOString();
    setBusyId(post.id);
    try {
      const next = await setPostSchedule(post.id, iso);
      patchPost(post.id, next);
      setScheduleId(null);
      toast.success(
        isPast
          ? 'Published, now live.'
          : `Scheduled for ${when.toLocaleString()} (your local time).`,
      );
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not schedule.');
    } finally {
      setBusyId(null);
    }
  }

  // Drag a scheduled chip onto a day → keep its time-of-day, move to that date.
  async function rescheduleToDay(post: Post, day: Date) {
    const base = post.published_at ? new Date(post.published_at) : new Date();
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), base.getHours(), base.getMinutes(), 0, 0);
    if (post.published_at && new Date(post.published_at).getTime() === next.getTime()) return; // same slot
    // Dropping onto a past day would publish it immediately - confirm first.
    if (next.getTime() <= Date.now() && !window.confirm(
      `${next.toLocaleString()} is in the past, so this will publish immediately. Continue?`,
    )) {
      return;
    }
    const iso = next.toISOString();
    setBusyId(post.id);
    try {
      const stored = await setPostSchedule(post.id, iso);
      patchPost(post.id, stored);
      toast.success(`Moved to ${next.toLocaleDateString()}.`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not move the post.');
    } finally {
      setBusyId(null);
    }
  }

  function handleDropOnDay(cell: MonthCell) {
    const post = posts.find((p) => p.id === dragId);
    setDragId(null);
    setDragOverKey(null);
    // Only scheduled (not-yet-live) posts can be dragged to a new day.
    if (!post || statusOf(post, Date.now()) !== 'scheduled') return;
    void rescheduleToDay(post, cell.date);
  }

  async function handleDelete(post: Post) {
    if (!window.confirm(`Delete "${post.title}"? This permanently removes the post and can't be undone.`)) return;
    setBusyId(post.id);
    try {
      await adminDeletePost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setSelected((s) => (s && s.id === post.id ? null : s));
      toast.success('Post deleted.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not delete the post.');
    } finally {
      setBusyId(null);
    }
  }

  function openSchedule(post: Post) {
    setScheduleId(post.id);
    setScheduleValue(post.published_at ? toLocalInput(new Date(post.published_at)) : defaultScheduleValue());
  }

  // Open the calendar detail popover, priming the reschedule field with this
  // post's go-live time so "Reschedule" works even without editing the input.
  function openDetail(post: Post) {
    setSelected(post);
    openSchedule(post);
  }

  // ---------- Month navigation ----------
  function goPrevMonth() {
    if (monthIndex === 0) { setMonthIndex(11); setYear((y) => y - 1); }
    else setMonthIndex((m) => m - 1);
  }
  function goNextMonth() {
    if (monthIndex === 11) { setMonthIndex(0); setYear((y) => y + 1); }
    else setMonthIndex((m) => m + 1);
  }
  function goThisMonth() {
    setYear(today.getFullYear());
    setMonthIndex(today.getMonth());
  }
  const isCurrentMonth = year === today.getFullYear() && monthIndex === today.getMonth();

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Content</h2></div>
        <ul className="admin__posts" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="admin__post admin__post--skel">
              <div className="admin__post-meta">
                <Skeleton variant="line" width={80} height={10} />
                <div style={{ height: 6 }} />
                <Skeleton variant="text" width="70%" height={18} />
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Content</h2>
        <div className="cm__head-actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={refresh} disabled={loading}>
            {loading ? '…' : 'Refresh'}
          </button>
          <Link to="/admin/new" className="btn btn--primary btn--sm">+ New post</Link>
        </div>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      {/* Failed video encodes - surfaced by the cf-stream webhook. These play
          as a broken player for everyone until the video is re-uploaded. */}
      {failed.length > 0 && (
        <div className="cm__failed" role="alert">
          <p className="kicker">Video encode failed</p>
          <p className="cm__failed-lead">
            {failed.length} video{failed.length > 1 ? 's' : ''} failed to process on Cloudflare and won’t
            play. Re-upload {failed.length > 1 ? 'them on the affected posts' : 'it on the affected post'}.
          </p>
          <ul className="cm__failed-list">
            {failed.map((m) => (
              <li key={m.id} className="cm__failed-item">
                <span className="cm__failed-reason">{m.error_detail ?? 'Unknown encode error'}</span>
                <span className="cm__failed-meta">
                  {m.posts.length > 0 ? (
                    <>
                      Used on{' '}
                      {m.posts.map((p, i) => (
                        <span key={p.id}>
                          {i > 0 && ', '}
                          <Link to={`/admin/edit/${p.slug}`}>{p.title}</Link>
                        </span>
                      ))}
                    </>
                  ) : (
                    <>Not attached to a post · UID {m.provider_id.slice(0, 8)}</>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Controls: status tabs + closet filter + view toggle */}
      <div className="cm__controls">
        <div className="admin__tabs" role="tablist" aria-label="Content status">
          {(['all', 'draft', 'scheduled', 'published'] as StatusTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={statusTab === tab}
              className={`admin__tab ${statusTab === tab ? 'admin__tab--active' : ''}`}
              onClick={() => setStatusTab(tab)}
            >
              {tab === 'all' ? 'All' : STATUS_LABEL[tab]}
              <span className="admin__tab-count">{counts[tab]}</span>
            </button>
          ))}
        </div>

        <div className="cm__controls-right">
          <label className="cm__search">
            <span className="visually-hidden">Search posts</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts…"
              aria-label="Search posts"
            />
          </label>

          <label className="cm__closet-filter">
            <span className="visually-hidden">Filter by closet</span>
            <select value={closetFilter} onChange={(e) => setClosetFilter(e.target.value)} aria-label="Filter by closet">
              <option value="all">All closets</option>
              {drawers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </label>

          <div className="cm__view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`cm__view-btn ${view === 'list' ? 'cm__view-btn--on' : ''}`}
              aria-pressed={view === 'list'}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`cm__view-btn ${view === 'calendar' ? 'cm__view-btn--on' : ''}`}
              aria-pressed={view === 'calendar'}
              onClick={() => setView('calendar')}
            >
              Calendar
            </button>
          </div>
        </div>
      </div>

      {/* ---------- List view ---------- */}
      {view === 'list' && (
        filtered.length === 0 ? (
          <EmptyState
            tone="compact"
            align="start"
            eyebrow="Nothing here"
            headline={statusTab === 'all' ? 'No posts yet.' : `No ${STATUS_LABEL[statusTab as PostStatus].toLowerCase()} posts.`}
            body="Publish the first one."
            cta={<Link to="/admin/new" className="btn btn--primary btn--sm">+ New post</Link>}
          />
        ) : (
          <ul className="cm__list">
            {filtered.map((p) => {
              const st = statusOf(p, now);
              const cover = coverOf(p);
              const drawer = p.drawers?.[0]?.name ?? ' - ';
              return (
                <li key={p.id} className="cm__card">
                  <div className="cm__thumb" aria-hidden="true">
                    {cover ? <img src={cover} alt="" loading="lazy" /> : <span className="cm__thumb--blank" />}
                  </div>
                  <div className="cm__card-body">
                    <div className="cm__card-top">
                      <span className={`cm__status cm__status--${st}`}>{STATUS_LABEL[st]}</span>
                      <span className="cm__closet">{drawer}</span>
                    </div>
                    <h3 className="cm__title">{p.title}</h3>
                    <p className="cm__meta">
                      {st === 'scheduled' && p.published_at && `Goes live ${new Date(p.published_at).toLocaleString()}`}
                      {st === 'published' && p.published_at && `Published ${new Date(p.published_at).toLocaleDateString()}`}
                      {st === 'draft' && 'Not published'}
                      {` · ${p.like_count ?? 0} likes · ${p.comment_count ?? 0} comments`}
                    </p>

                    {scheduleId === p.id && (
                      <div className="cm__schedule-row">
                        <input
                          type="datetime-local"
                          value={scheduleValue}
                          onChange={(e) => setScheduleValue(e.target.value)}
                          aria-label="Schedule go-live time"
                        />
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => void handleSchedule(p)}
                          disabled={busyId === p.id}
                        >
                          {busyId === p.id ? '…' : 'Set time'}
                        </button>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setScheduleId(null)}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="cm__actions">
                    <Link to={`/p/${p.slug}`} className="btn btn--secondary btn--sm">View</Link>
                    <Link to={`/admin/edit/${p.slug}`} className="btn btn--secondary btn--sm">Edit</Link>

                    {st === 'published' ? (
                      // Live: only un-publishing makes sense (re-scheduling would hide it).
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => void handleSetLive(p, false)}
                        disabled={busyId === p.id}
                      >
                        {busyId === p.id ? '…' : 'Unpublish'}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => void handleSetLive(p, true)}
                          disabled={busyId === p.id}
                        >
                          {busyId === p.id ? '…' : 'Publish now'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => (scheduleId === p.id ? setScheduleId(null) : openSchedule(p))}
                          disabled={busyId === p.id}
                        >
                          {st === 'scheduled' ? 'Reschedule' : 'Schedule'}
                        </button>
                        {st === 'scheduled' && (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => void handleSetLive(p, false)}
                            disabled={busyId === p.id}
                          >
                            Back to draft
                          </button>
                        )}
                      </>
                    )}

                    <button
                      type="button"
                      className="btn btn--ghost btn--sm cm__delete"
                      onClick={() => void handleDelete(p)}
                      disabled={busyId === p.id}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      )}

      {/* ---------- Calendar view ---------- */}
      {view === 'calendar' && (
        <div className="cm__calendar">
          <div className="cm__cal-head">
            <h3 className="cm__cal-month">{MONTH_FMT.format(new Date(year, monthIndex, 1))}</h3>
            <div className="cm__cal-nav" role="group" aria-label="Month navigation">
              <button type="button" className="btn btn--ghost btn--sm" onClick={goPrevMonth} aria-label="Previous month">← Prev</button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={goThisMonth} disabled={isCurrentMonth}>Today</button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={goNextMonth} aria-label="Next month">Next →</button>
            </div>
          </div>

          <p className="admin__note">
            Shows scheduled + published posts on their go-live date{closetFilter !== 'all' ? ' in the selected closet' : ''}.
            Drafts have no date; find them under the Draft tab.
          </p>
          <div className="cm__cal-legend">
            <span className="cm__cal-legend-item">
              <span className="cm__cal-legend-swatch cm__cal-chip--scheduled" aria-hidden="true">◷</span>
              Scheduled · drag to reschedule
            </span>
            <span className="cm__cal-legend-item">
              <span className="cm__cal-legend-swatch cm__cal-chip--published" aria-hidden="true">✓</span>
              Published
            </span>
          </div>

          {/* Feed reset cadence - when the whole wall resets (doors shut, then
              pop open as this cycle's content releases). */}
          <div className="cm__cadence">
            <span className="cm__cadence-label">Feed reset</span>
            <select
              className="cm__cadence-select"
              value={cycleType}
              onChange={(e) => setCycleType(e.target.value as 'off' | CadenceType)}
              disabled={cycleSaving}
              aria-label="Feed reset cadence"
            >
              <option value="off">No monthly reset</option>
              <option value="last_day">Last day of the month</option>
              <option value="day_of_month">A specific day…</option>
            </select>
            {cycleType === 'day_of_month' && (
              <input
                className="cm__cadence-day"
                type="number"
                min={1}
                max={31}
                value={cycleDay}
                onChange={(e) => setCycleDay(Math.min(31, Math.max(1, Number.parseInt(e.target.value, 10) || 1)))}
                disabled={cycleSaving}
                aria-label="Day of month"
              />
            )}
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => void saveCadence()}
              disabled={!cadenceDirty || cycleSaving}
            >
              {cycleSaving ? '…' : 'Save'}
            </button>
            <span className="cm__cadence-hint">
              {cycleRule
                ? `Resets ${describeRule(cycleRule)} · next ${nextReset(cycleRule)?.toLocaleDateString()}`
                : 'No reset: content stays on the wall until replaced.'}
            </span>
          </div>

          <div className="cm__cal-weekdays">
            {DAY_LABELS.map((d) => <div key={d} className="cm__cal-weekday">{d}</div>)}
          </div>
          <div className="cm__cal-grid" role="grid">
            {cells.map((cell, i) => {
              const key = dayKey(cell.date);
              const isResetDay = cell.inMonth && resetDay != null && cell.date.getDate() === resetDay;
              return (
                <div
                  key={i}
                  className={[
                    'cm__cal-cell',
                    cell.inMonth ? '' : 'cm__cal-cell--outside',
                    cell.isToday ? 'cm__cal-cell--today' : '',
                    isResetDay ? 'cm__cal-cell--reset' : '',
                    dragOverKey === key ? 'cm__cal-cell--drop' : '',
                  ].filter(Boolean).join(' ')}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setDragOverKey(key); } }}
                  onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
                  onDrop={(e) => { e.preventDefault(); handleDropOnDay(cell); }}
                >
                  <span className="cm__cal-num">
                    {cell.date.getDate()}
                    {isResetDay && <span className="cm__cal-reset-tag" title="Feed resets this day">⟳</span>}
                  </span>
                  <div className="cm__cal-chips">
                    {cell.posts.map((p) => {
                      const st = statusOf(p, now);
                      const draggable = st === 'scheduled';
                      return (
                        <button
                          key={p.id}
                          type="button"
                          draggable={draggable}
                          onDragStart={(e) => { setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => { setDragId(null); setDragOverKey(null); }}
                          className={`cm__cal-chip cm__cal-chip--${st} ${draggable ? 'cm__cal-chip--draggable' : ''}`}
                          onClick={() => openDetail(p)}
                          title={`${STATUS_LABEL[st]} · ${p.title}${draggable ? ', drag to reschedule' : ''}`}
                        >
                          <span className="cm__cal-chip-icon" aria-hidden="true">{st === 'scheduled' ? '◷' : '✓'}</span>
                          <span className="cm__cal-chip-label">{p.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- Calendar detail popover ---------- */}
      {selected && (
        <div className="cm__modal" role="dialog" aria-modal="true" aria-label={selected.title} onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="cm__modal-panel">
            <button type="button" className="cm__modal-close" onClick={() => setSelected(null)} aria-label="Close">×</button>
            <span className={`cm__status cm__status--${statusOf(selected, now)}`}>{STATUS_LABEL[statusOf(selected, now)]}</span>
            <h3 className="cm__modal-title">{selected.title}</h3>
            <p className="cm__meta">
              {selected.drawers?.[0]?.name ?? ' - '}
              {selected.published_at && ` · ${new Date(selected.published_at).toLocaleString()}`}
            </p>

            {/* Reschedule only applies to a not-yet-live (scheduled) post. */}
            {statusOf(selected, now) === 'scheduled' && (
              <div className="cm__modal-schedule">
                <label className="cm__modal-label" htmlFor="cm-resched">Go live at</label>
                <input
                  id="cm-resched"
                  type="datetime-local"
                  value={scheduleValue}
                  onChange={(e) => { setScheduleId(selected.id); setScheduleValue(e.target.value); }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => void handleSchedule(selected)}
                  disabled={busyId === selected.id}
                >
                  {busyId === selected.id ? '…' : 'Reschedule'}
                </button>
              </div>
            )}

            <div className="cm__modal-actions">
              <Link to={`/p/${selected.slug}`} className="btn btn--secondary btn--sm">View</Link>
              <Link to={`/admin/edit/${selected.slug}`} className="btn btn--secondary btn--sm">Edit</Link>
              {statusOf(selected, now) === 'published' ? (
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => void handleSetLive(selected, false)} disabled={busyId === selected.id}>
                  Unpublish
                </button>
              ) : (
                <>
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => void handleSetLive(selected, true)} disabled={busyId === selected.id}>
                    Publish now
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void handleSetLive(selected, false)} disabled={busyId === selected.id}>
                    Back to draft
                  </button>
                </>
              )}
              <button type="button" className="btn btn--ghost btn--sm cm__delete" onClick={() => void handleDelete(selected)} disabled={busyId === selected.id}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
